<template>
  <div class="service">
    <h2 class="h4">Welche Dienstleistung möchten Sie in Anspruch nehmen?</h2>
    <p class="service__subtitle">Sie können maximal zwei Dienstleistungen wählen</p>
    <el-checkbox-group v-model="selectedNames" :max="2">
      <h3 class="service__category">Unsere Beliebtesten</h3>
      <template v-for="(service, index) in services?.oesterreichweit.items" :key="service.snrId">
        <el-checkbox
          :label="service.snrBezeichnung"
          @change="checkboxCheckHandler($event, service.snrBezeichnung, service.snrId)"
        />
        <div class="service__info" v-if="selectedIds.includes(service.snrId)">
          <p v-if="service.servicehint">
            {{ service.servicehint }}
          </p>
        </div>
        <hr v-if="index !== services?.oesterreichweit.totalElements - 1" />
      </template>
      <h3 v-if="otherServices" class="service__category">Weitere Angebote</h3>
      <template v-for="(service, index) in otherServices" :key="service.snrId">
        <el-checkbox
          :label="service.snrBezeichnung"
          @change="checkboxCheckHandler($event, service.snrBezeichnung, service.snrId)"
        />
        <div class="service__info" v-if="selectedIds.includes(service.snrId)">
          <p v-if="service.servicehint">
            {{ service.servicehint }}
          </p>
        </div>
        <hr v-if="index !== otherServices.length - 1" />
      </template>
    </el-checkbox-group>
    <div class="service__all">
      <template v-if="services?.ausgewaehlterLandesclub?.totalElements > 3">
        <button class="service__all--clickable" @click="showMore" v-if="!showAll">
          <p>Alle anzeigen</p>
          <IconChevronDown aria-hidden="true" />
        </button>
        <button class="service__all--clickable" @click="showLess" v-else>
          <p>Weniger anzeigen</p>
          <IconChevronUp aria-hidden="true" />
        </button>
      </template>
    </div>
    <Navigation :isNextButtonDisabled="!selectedNames.length || !services" />
  </div>
</template>

<script>
import Navigation from '@/components/Booking/Navigation.vue'
import IconChevronDown from '@/components/icons/IconChevronDown.vue'
import IconChevronUp from '@/components/icons/IconChevronUp.vue'
import { trackGa4Event } from '@/utils/tracking'
import { mapGetters } from 'vuex'

export default {
  components: { IconChevronDown, IconChevronUp, Navigation },
  data() {
    return {
      selectedNames: [],
      selectedIds: [],
      showAll: false,
    }
  },
  activated() {
    if (this.services) {
      this.populateDataFromStore()
    } else {
      if (this.sessionData) this.$store.dispatch('getServices')
    }

    // User lands on the first step of the TVG 'Angebot'
    trackGa4Event({
      ga4Event: 'tvg_step_1',
      ga4ParameterName: 'region',
      ga4ParameterValue: this.selectedState.bezeichnung,
    })
  },
  computed: {
    ...mapGetters([
      'sessionData',
      'selectedState',
      'services',
      'selectedServicesId',
      'selectedServicesName',
    ]),
    otherServices() {
      if (this.services?.ausgewaehlterLandesclub?.totalElements <= 3)
        return this.services?.ausgewaehlterLandesclub?.items

      return this.showAll
        ? this.services?.ausgewaehlterLandesclub?.items
        : this.services?.ausgewaehlterLandesclub?.items.slice(0, 3)
    },
    isPreviousDataPopulated() {
      return this.sessionData && this.selectedState
    },
  },
  watch: {
    isPreviousDataPopulated() {
      // on entering url, wait until sessionData is loaded and state poulated
      if (this.isPreviousDataPopulated) this.$store.dispatch('getServices')
    },
    services() {
      if (this.services) this.populateDataFromStore()
    },
  },
  methods: {
    populateDataFromStore() {
      this.selectedIds = this.selectedServicesId
      this.selectedNames = this.selectedServicesName // from reload or going back

      // if id is not in initial display, show all checkboxes
      const undisplayedIds = this.services.ausgewaehlterLandesclub?.items
        .slice(3)
        .map((service) => service.snrId)
      if (this.selectedServicesId.some((id) => undisplayedIds.includes(id))) this.showAll = true

      const combinedServices = this.services.ausgewaehlterLandesclub?.items
        ? this.services.oesterreichweit.items.concat(this.services.ausgewaehlterLandesclub.items)
        : this.services.oesterreichweit.items

      const selectedServicesName = combinedServices
        .filter((service) => this.selectedServicesId.includes(service.snrId))
        .map((service) => service.snrBezeichnung)
      this.selectedNames = selectedServicesName // from rebooking
      this.$store.commit('setSelectedServicesName', this.selectedNames)
    },
    checkboxCheckHandler(checked, label, id) {
      if (checked) {
        this.selectedIds.push(+id)

        // User clicks on a service and adds it
        trackGa4Event({
          ga4Event: 'tvg_add_service',
          ga4ParameterName: 'service',
          ga4ParameterValue: label,
        })
      } else {
        this.selectedIds = this.selectedIds.filter((selectedId) => selectedId !== +id)

        // User clicks on a service again to remove it
        trackGa4Event({
          ga4Event: 'tvg_remove_service',
          ga4ParameterName: 'service',
          ga4ParameterValue: label,
        })
      }

      this.$store.commit('setSelectedServicesId', this.selectedIds)
      this.$store.commit('setSelectedServicesName', this.selectedNames)
    },
    showMore() {
      this.showAll = true

      // User clicks on 'Alle Anzeigen'
      trackGa4Event({
        ga4Event: 'tvg_show_all',
        ga4ParameterName: 'action',
        ga4ParameterValue: 'open',
      })
    },
    showLess() {
      this.showAll = false
      window.scrollTo(0, 0)
    },
  },
}
</script>

<style lang="scss">
.service {
  h4 {
    margin-bottom: 4px;
  }

  &__subtitle {
    @include subtitle;
    margin: 0;
  }

  .el-checkbox-group {
    display: flex;
    flex-direction: column;
  }

  #content &__category,
  &__category {
    font-size: 12px;
    line-height: 1.75;
    margin-top: 24px;
    margin-bottom: 4px;
    font-weight: $font-weight-regular;
  }

  &__info {
    color: $color-text-secondary;
    margin-left: 36px;

    p {
      margin: 8px 0 15px;
    }
  }

  &__all {
    @include flex-center-justify;
    margin: 16px 0 40px;

    &--clickable {
      color: $color-text-secondary;
      cursor: pointer;
      display: flex;
      border-radius: 4px;

      &:hover,
      &:focus-visible {
        text-decoration: underline;
      }

      p {
        margin-right: 2px;
      }
    }
  }
}
</style>
